import React, { useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { 
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const SettingsPage: React.FC = () => {
  const { resetApp, validateApp, isInitializing } = useAppStore();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    issues: string[];
  } | null>(null);

  const handleValidateSystem = async () => {
    setIsValidating(true);
    try {
      const result = await validateApp();
      setValidationResult(result);
    } catch (error) {
      setValidationResult({
        isValid: false,
        issues: ['验证过程中发生错误']
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleResetSystem = async () => {
    if (window.confirm('确定要重置系统吗？这将清除所有数据并重新初始化。')) {
      await resetApp();
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900">系统设置</h1>
        <p className="text-gray-600 mt-1">系统配置、维护和诊断工具</p>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Cog6ToothIcon className="w-5 h-5 mr-2" />
          系统状态
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-800">系统验证</div>
              <div className="text-sm text-gray-600">检查系统完整性和数据一致性</div>
            </div>
            <button
              onClick={handleValidateSystem}
              disabled={isValidating}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
            >
              {isValidating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>验证中...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>开始验证</span>
                </>
              )}
            </button>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className={`p-4 rounded-lg border ${
              validationResult.isValid 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center mb-2">
                {validationResult.isValid ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
                )}
                <span className={`font-medium ${
                  validationResult.isValid ? 'text-green-800' : 'text-red-800'
                }`}>
                  {validationResult.isValid ? '系统状态正常' : '发现系统问题'}
                </span>
              </div>
              
              {validationResult.issues.length > 0 && (
                <div className="space-y-1">
                  {validationResult.issues.map((issue, index) => (
                    <div key={index} className="text-sm text-red-700">
                      • {issue}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* System Maintenance */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <ArrowPathIcon className="w-5 h-5 mr-2" />
          系统维护
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div>
              <div className="font-medium text-yellow-800">重置系统</div>
              <div className="text-sm text-yellow-700">
                清除所有数据并重新初始化系统（谨慎操作）
              </div>
            </div>
            <button
              onClick={handleResetSystem}
              disabled={isInitializing}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
            >
              {isInitializing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>重置中...</span>
                </>
              ) : (
                <>
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  <span>重置系统</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">系统信息</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">版本</div>
            <div className="font-medium text-gray-800">v1.0.0</div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">构建时间</div>
            <div className="font-medium text-gray-800">2024-01-01</div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">数据库</div>
            <div className="font-medium text-gray-800">Supabase PostgreSQL</div>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">前端框架</div>
            <div className="font-medium text-gray-800">React + TypeScript</div>
          </div>
        </div>
      </div>

      {/* Help and Support */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">帮助与支持</h2>
        
        <div className="space-y-3 text-sm text-gray-600">
          <div>
            <strong>使用说明：</strong>
            <ul className="mt-1 ml-4 space-y-1">
              <li>• 在"人员管理"中添加或移除参赛者</li>
              <li>• 在"队列管理"中查看轮换状态并进行轮换</li>
              <li>• 在"比赛管理"中生成配对并记录结果</li>
              <li>• 在"数据统计"中查看详细的统计分析</li>
            </ul>
          </div>
          
          <div>
            <strong>注意事项：</strong>
            <ul className="mt-1 ml-4 space-y-1">
              <li>• 至少需要4个活跃玩家才能进行比赛</li>
              <li>• 比赛结果一旦保存无法修改</li>
              <li>• 重置系统将清除所有历史数据</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;